import { useSetRecoilState } from 'recoil';
import { object, string } from 'yup';
import { Formik } from 'formik';

import * as K from 'common/constants';
import Input from 'components/common/Input/Input';
import ELEMENT from 'common/element';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { FormState } from '../types';
import '../styles.scss';

type LinkAccountFormProps = {
  onSubmit: (formState: FormState) => void;
  isSSOAuthenticated: boolean;
};

const initialValues: FormState = {
  email: '',
  password: '',
};

export const LinkAccountForm = ({ onSubmit, isSSOAuthenticated }: LinkAccountFormProps) => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const emailValidationSchema = string().defined().trim().email('Please enter a valid email');
  const passwordValidationSchema = string().optional();

  const validationSchema = object(
    isSSOAuthenticated
      ? {
          email: emailValidationSchema,
          password: passwordValidationSchema,
        }
      : {
          email: emailValidationSchema,
          password: passwordValidationSchema.required('Please enter a password'),
        },
  );

  const onSubmitHandler = (values: FormState): void => {
    setPageAlertConfig(K.NO_PAGE_ALERT);
    // @ts-expect-error even thought email is marked as defined, when casting it's value is getting `string | undefined`
    onSubmit(validationSchema.cast(values));
  };

  return (
    <Formik validationSchema={validationSchema} onSubmit={onSubmitHandler} initialValues={initialValues}>
      {({ submitForm, handleChange, values, errors, touched }): JSX.Element => {
        const isEmailError = touched.email && Boolean(errors.email);

        const trimmedEmail = values.email.trim();

        return (
          <div className="LinkAccount__Form">
            {isSSOAuthenticated ? (
              <div className="LinkAccount__Form--Button">
                <Input
                  name="email"
                  value={trimmedEmail}
                  onChange={handleChange}
                  label="Account to Use"
                  inputClassName="LinkAccount__Form--Input"
                  labelClassName="LinkAccount__Form--Field LinkAccount__Form--Account"
                  labelTextClassName="LinkAccount__Form--Label"
                  errorTextClassName="LinkAccount__Form--InvalidText"
                  isInvalid={isEmailError}
                  errorMessage={errors.email}
                  testId={ELEMENT.linkAccountPageEmailInput}
                />
                <Button onClick={submitForm} type="submit" variant={ButtonVariant.PRIMARY}>
                  Set Link
                </Button>
              </div>
            ) : (
              <>
                <Input
                  name="email"
                  value={trimmedEmail}
                  onChange={handleChange}
                  label="Account to Use"
                  inputClassName="LinkAccount__Form--Input"
                  labelClassName="LinkAccount__Form--Field LinkAccount__Form--Account"
                  labelTextClassName="LinkAccount__Form--Label"
                  errorTextClassName="LinkAccount__Form--InvalidText"
                  isInvalid={isEmailError}
                  errorMessage={errors.email}
                  testId={ELEMENT.linkAccountPageEmailInput}
                />
                <Input
                  name="password"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  label="Your Password"
                  inputClassName="LinkAccount__Form--Input"
                  labelClassName="LinkAccount__Form--Field LinkAccount__Form--Password "
                  labelTextClassName="LinkAccount__Form--Label"
                  errorTextClassName="LinkAccount__Form--InvalidText"
                  isInvalid={touched.password && !!errors.password}
                  errorMessage={errors.password}
                  testId={ELEMENT.linkAccountPagePasswordInput}
                />
                <Button
                  onClick={submitForm}
                  type="submit"
                  variant={ButtonVariant.PRIMARY}
                  testId={ELEMENT.linkAccountPageSetLinkButton}
                >
                  Set Link
                </Button>
              </>
            )}
          </div>
        );
      }}
    </Formik>
  );
};
